import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "src/lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground shadow-sm hover:bg-primary-dark hover:shadow-md hover:-translate-y-px transition-all duration-200",
				destructive:
					"bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
				outline:
					"border border-border bg-white text-foreground shadow-xs hover:bg-foreground hover:text-white hover:border-foreground transition-colors",
				destructiveOutline:
					"border border-destructive/40 bg-white text-destructive shadow-xs hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors",
				secondary: "bg-accent text-foreground shadow-sm hover:bg-accent/80",
				ghost:
					"border border-border bg-white text-foreground shadow-xs hover:bg-foreground hover:text-white hover:border-foreground transition-colors",
				link: "text-foreground underline-offset-4 hover:underline"
			},
			// One height for every variant — already flattened before this, now
			// raised 40px → 44px so buttons match the admin's text fields
			// (Input `lg`, ContentTable's ROW_CONTROL_HEIGHT) instead of sitting
			// 4px shorter beside them. 44px is also Apple's HIG touch-target
			// guidance; WCAG 2.2 AA only requires 24px, so this is consistency
			// and comfort rather than a compliance fix.
			size: {
				default: "h-11 px-5 py-2",
				sm: "h-11 px-5 py-2",
				lg: "h-11 px-5 py-2",
				icon: "h-11 w-11"
			}
		},
		defaultVariants: {
			variant: "default",
			size: "default"
		}
	}
);

export interface ButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	/**
	 * When true, disables the button and replaces its content with a spinner
	 * that inherits the button's text colour (`border-current`), so it works
	 * on every variant. Ignored when `asChild` is set.
	 */
	isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			isLoading = false,
			disabled,
			children,
			...props
		},
		ref
	) => {
		const Comp = asChild ? Slot : "button";
		const showSpinner = isLoading && !asChild;
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				disabled={disabled || showSpinner}
				aria-busy={showSpinner || undefined}
				{...props}
			>
				{showSpinner ? (
					<>
						<span
							className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
							aria-hidden="true"
						/>
						{/* keep the label as the accessible name while loading */}
						<span className="sr-only">{children}</span>
					</>
				) : (
					children
				)}
			</Comp>
		);
	}
);
Button.displayName = "Button";

export { Button, buttonVariants };
